import { twMerge } from 'tailwind-merge'
import classNames from 'classnames'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/pt-br'
import utc from 'dayjs/plugin/utc.js'
import Button from '@/components/atoms/Button'
import Icon from '@/components/atoms/Icon'

dayjs.extend(utc)
dayjs.locale('pt-br')

interface ProgressCheck {
	id: number
	turma_id: number
	data_inicio: string
	data_ultimo_envio: string
	submitted: Array<number>
	total_tentativas?: number
}

interface CardProgressCheckProps {
	progressCheck: ProgressCheck
	stepId: number
	isPast: boolean
	nextSubmitLimitStart?: Dayjs
	notStart?: boolean
	isLoading?: boolean
	className?: string
	onClick?: () => void
}

// Função para pegar a maior nota enviada
const getMaxNota = (submitted: Array<number>): number => {
	return Math.max(...submitted)
}

const getStatusArray = (
	submitted: Array<number>
): Array<'melhorou' | 'piorou' | 'manteve' | null> => {
	if (submitted.length === 0) return [null] // Retorna um array com null se não houver tentativas

	// Cria um novo array que contém os status
	const statusArray: Array<'melhorou' | 'piorou' | 'manteve'> = submitted
		.slice(1)
		.map((notaAtual, i) => {
			const notaAnterior = submitted[i] // Pega a nota anterior do array original

			// Determina o status
			return notaAtual > notaAnterior
				? 'melhorou'
				: notaAtual < notaAnterior
					? 'piorou'
					: 'manteve'
		})

	// Adiciona um valor inicial para a primeira tentativa
	return [null, ...statusArray] // Insere null no início do array de status
}

export function CardProgressCheck({
	progressCheck,
	stepId,
	isPast,
	nextSubmitLimitStart,
	notStart,
	isLoading,
	className,
	onClick,
}: CardProgressCheckProps) {
	const tentativaLiberada =
		stepId === 1 || progressCheck.submitted.length >= stepId - 1
	const tentativaEnviada = progressCheck.submitted.length >= stepId

	const uniqueNumbers = progressCheck.submitted.reduce<number[]>((acc, num) => {
		if (acc.includes(num)) {
			acc.push(0)
		} else {
			acc.push(num)
		}
		return acc
	}, [])

	const notaAtual = uniqueNumbers[stepId - 1] ?? 0
	const maiorNota = getMaxNota(progressCheck.submitted)

	const statusArray = tentativaEnviada
		? getStatusArray(progressCheck.submitted)
		: null

	const status = statusArray?.length ? statusArray[stepId - 1] : ''

	const iconMap: Record<string, string> = {
		melhorou: 'arrow-up-2',
		piorou: 'arrow-down-2',
	}

	return (
		<div
			className={twMerge(
				'relative flex flex-col justify-between bg-[#312B48] min-h-[208px] border-[6px] border-solid border-brand-primary opacity-100 rounded-xl p-5 transition-all duration-500',
				classNames(className, {
					'animate-pulse pointer-events-none': isLoading,
					'border-transparent opacity-30 pointer-events-none':
						!tentativaLiberada || isPast || notStart,
					'bg-[#F5F2FF] shadow-card-progress-check pointer-events-none':
						tentativaEnviada,
					'border-white/20 border-2':
						!dayjs().isSame(dayjs(nextSubmitLimitStart).endOf('day'), 'day') &&
						!dayjs().isAfter(dayjs(nextSubmitLimitStart).endOf('day'), 'day') &&
						!tentativaEnviada,
					'opacity-30': notaAtual !== maiorNota && tentativaEnviada,
				})
			)}
		>
			{status &&
				['melhorou', 'manteve'].includes(status) &&
				notaAtual === maiorNota && (
					<Icon
						name='you-rock'
						width='220px'
						height='190px'
						className='absolute -top-24 -right-12 z-40'
					/>
				)}
			<div className='flex items-center justify-between'>
				<div className='flex flex-col gap-1'>
					<span
						className={twMerge(
							classNames('text-white text-xs font-semibold', {
								hidden:
									!tentativaLiberada ||
									tentativaEnviada ||
									(!dayjs().isSame(
										dayjs(nextSubmitLimitStart).endOf('day'),
										'day'
									) &&
										!dayjs().isAfter(
											dayjs(nextSubmitLimitStart).endOf('day'),
											'day'
										)),
							})
						)}
					>
						DISPONÍVEL
					</span>
					<Icon
						name='check-content'
						width='24px'
						height='24px'
						className={twMerge(
							classNames('hidden', {
								'block mb-2': tentativaEnviada,
							})
						)}
					/>
					<p
						className={twMerge(
							classNames('text-[28px] font-bold text-[#F5F2FF]', {
								'text-[#150D31]': tentativaEnviada,
							})
						)}
					>
						{stepId}ª Tentativa
					</p>
				</div>
				<Icon
					name='lock-2'
					width='24px'
					height='24px'
					className={twMerge(
						classNames('hidden', {
							block: !tentativaLiberada || tentativaEnviada,
						})
					)}
				/>
			</div>
			{(() => {
				if (tentativaLiberada && !tentativaEnviada && !isPast) {
					return (
						<div className='flex flex-col gap-3'>
							{!dayjs().isAfter(dayjs(nextSubmitLimitStart), 'day') &&
								!dayjs().isSame(dayjs(nextSubmitLimitStart), 'day') && (
									<p className='text-sm text-[#F5F2FF]'>
										Disponível em{' '}
										<b>
											{dayjs(nextSubmitLimitStart)
												.endOf('day')
												.locale('pt-br')
												.format('DD/MM')}
										</b>
									</p>
								)}
							<Button
								className='rounded-full px-4'
								onClick={onClick}
								disabled={
									!dayjs().isSame(
										dayjs(nextSubmitLimitStart).endOf('day'),
										'day'
									) && !dayjs().isAfter(dayjs(nextSubmitLimitStart), 'day')
								}
							>
								<p className='font-semibold text-white'>
									Iniciar Progress Check
								</p>
							</Button>
						</div>
					)
				}

				if (tentativaEnviada)
					return (
						<div className='flex flex-col gap-4 mt-7'>
							<p className='text-sm text-[#150D31]/60 font-semibold'>
								SEU RESULTADO
							</p>
							<div className='flex flex-col gap-1'>
								<div className='flex items-center gap-3'>
									<p className='text-5xl text-brand-primary font-bold'>
										{progressCheck.submitted[stepId - 1]}%
									</p>
									<Icon
										name={(status && iconMap[status]) || ''}
										width='20px'
										height='20px'
										className={twMerge(
											status && iconMap[status] ? 'block' : 'hidden'
										)}
									/>
								</div>
								<p className='text-sm text-[#150D31]/60 font-semibold'>
									% DE APROVEITAMENTO
								</p>
							</div>
						</div>
					)

				return (
					<p className='text-sm text-white'>
						Complete a {stepId - 1}ª Tentativa para desbloquear
					</p>
				)
			})()}
		</div>
	)
}
